import { SUPER_PATHS } from "@router/superRoutes";
import type { NavigationItem, NavigationSubItem } from "@types";
import {
  Activity,
  Building2,
  DatabaseZap,
  GitBranch,
  GraduationCap,
  LandPlot,
  MessagesSquare,
  NotebookPen,
  PieChart,
  Puzzle,
  SearchCheck,
  Settings,
  TrendingUp,
  Users2,
} from "lucide-react";

type SidebarSection = {
  title: string;
  items: NavigationItem[];
};

type NavigationSubMenus = {
  settings: NavigationSubItem[];
  coach: NavigationSubItem[];
  phone: NavigationSubItem[];
};

type Config = {
  navigation: {
    sidebar: SidebarSection[];
    subMenus: NavigationSubMenus;
  };
  industry: {
    options: string[];
  };
  language: {
    options: Record<string, string>[];
  };
  backgroundSound: {
    options: { id: string; name: string }[];
  };
};

// The Settings workspace merges the old Company and Account sections
// (/company/* + /account/* → /settings/*; old URLs redirect in router.tsx).
// Members reach this workspace for their notifications and archived chats, so
// every section names its roles: an unroled one would list a page the route
// guard denies them.
const settingsSubMenu: NavigationSubItem[] = [
  // Super Settings moved into the Super hub as first-class destinations
  // (Company settings group — SD-10928); see superNav.ts.
  {
    href: "/settings",
    title: "Info",
    roles: new Set(["super", "admin", "owner"]),
  },
  {
    href: "/settings/hours",
    title: "Hours",
    roles: new Set(["super", "admin", "owner"]),
  },
  {
    href: "/settings/holidays",
    title: "Holidays",
    roles: new Set(["super", "admin", "owner"]),
  },
  {
    href: "/settings/service-areas",
    title: "Service areas",
    roles: new Set(["super", "admin", "owner"]),
  },
  {
    href: "/settings/custom-analysis",
    title: "Custom analysis",
    roles: new Set(["super", "admin", "owner"]),
  },
  {
    href: "/settings/transfer-destinations",
    title: "AI transfer destinations",
    roles: new Set(["super", "admin", "owner"]),
  },
  // The last FSM-agnostic Services pages moved here when the standalone
  // Services nav retired (SD-10920) — the FSM-specific pages already live in
  // their integration workspaces. The old /services and /services/pricing-lookup
  // URLs redirect (see router.tsx); the FSM /services/* deep links still resolve.
  {
    href: "/settings/custom-services",
    title: "Custom services",
    roles: new Set(["super", "admin", "owner"]),
  },
  {
    href: "/settings/pricing-lookup",
    title: "Pricing lookup",
    roles: new Set(["super", "admin", "owner"]),
  },
  {
    href: "/settings/services-not-offered",
    title: "Services not offered",
    roles: new Set(["super", "admin", "owner"]),
  },
  {
    href: "/settings/faqs",
    title: "FAQs",
    roles: new Set(["super", "admin", "owner"]),
  },
  {
    href: "/settings/reports",
    title: "Reports",
    roles: new Set(["super", "admin", "owner"]),
  },
  // Text Messaging moved to the Phone workspace (FE-1).
  // Compliance (call-recording consent) moved to Phone → Compliance.
  // Data export + the product-analytics opt-out (former Account -> Advanced).
  // Admins open it for the do-not-contact list only; the page keeps its other
  // sections to owners and super.
  {
    href: "/settings/data-privacy",
    title: "Data & privacy",
    roles: new Set(["super", "owner", "admin"]),
  },
  // Call-recording consent, one section per direction. Same gate as Data &
  // Privacy's own sections — they are the same "what we collect and on what
  // basis" concern.
  {
    href: "/settings/inbound-consent",
    title: "Inbound consent",
    roles: new Set(["super", "owner"]),
  },
  {
    href: "/settings/outbound-consent",
    title: "Outbound consent",
    roles: new Set(["super", "owner"]),
  },
  // Former Account section (Phone numbers + Texting moved to the Phone
  // workspace in FE-1).
  {
    href: "/settings/team",
    title: "Team",
    roles: new Set(["super", "admin", "owner"]),
  },
  {
    href: "/settings/billing",
    title: "Billing and usage",
    roles: new Set(["super", "admin", "owner"]),
  },
  // Everything Sameday sends to users, in one place — per-teammate alert
  // preferences (formerly buried in the Team drawer) plus pointers to the
  // company-wide alert streams. Members get their own row only (SD-19135).
  {
    href: "/settings/notifications",
    title: "Notifications",
    roles: new Set(["super", "admin", "owner", "member"]),
  },
  {
    href: "/settings/agent-memory",
    title: "Agent memory",
    roles: new Set(["admin", "owner", "member"]),
  },
  {
    href: "/settings/archived-chats",
    title: "Archived chats",
    roles: new Set(["admin", "owner", "member"]),
  },
  // Account-level telephony (SD-15879). These left the Phone workspace because
  // they serve every account — AI-voice, forwarding, and texting-only ones —
  // not just Phone System customers. The Phone workspace keeps its own richer
  // "Numbers" view of the same numbers.
  {
    href: "/settings/phone-numbers",
    title: "Phone numbers",
    roles: new Set(["super", "admin", "owner"]),
  },
  {
    href: "/settings/texting-registration",
    title: "Texting registration",
    roles: new Set(["super", "admin", "owner"]),
  },
  // Account-scoped blocked callers (SD-17708). Gated to the same roles dale
  // requires for the write (`is_staff or role in {owner, admin}`), so everyone
  // who can open the page can manage it.
  {
    href: "/settings/blocked-callers",
    title: "Blocked callers",
    roles: new Set(["super", "admin", "owner"]),
  },
];

// Phone workspace (FE-1): consolidates the account/company phone features under
// /phone/*. Every section, including the Overview, is gated to super/admin/owner
// for now — mirroring the pre-consolidation routes (kept in sync with
// routeConfig.ts). Later phone-system WPs loosen this behind the
// phoneSystemEnabled flag (seated members get the workspace).
const phoneSubMenu: NavigationSubItem[] = [
  {
    href: "/phone/dashboard",
    title: "Dashboard",
    roles: new Set(["super", "admin", "owner"]),
  },
  {
    href: "/phone/calls",
    title: "History",
    roles: new Set(["super", "admin", "owner"]),
  },
  {
    href: "/phone/seats",
    title: "Seats",
    roles: new Set(["super", "admin", "owner"]),
  },
  {
    href: "/phone/queues",
    title: "Call routing",
    roles: new Set(["super", "admin", "owner"]),
  },
  {
    href: "/phone/numbers",
    title: "Numbers",
    roles: new Set(["super", "admin", "owner"]),
  },
  {
    href: "/phone/port-orders",
    title: "Port orders",
    roles: new Set(["super", "admin", "owner"]),
  },
  // Phone → Notifications (AI text alerts) consolidated into
  // Settings → Notifications; the /phone/notifications URL redirects there.
  {
    href: "/phone/compliance",
    title: "Compliance",
    roles: new Set(["super", "admin", "owner"]),
  },
  {
    href: "/phone/my-settings",
    title: "My settings",
    roles: new Set(["super", "admin", "owner", "member"]),
  },
];

const coachSubMenu: NavigationSubItem[] = [
  { href: "/coach/overview", title: "Overview" },
  { href: "/coach/conversations", title: "Conversations" },
  { href: "/coach/agent-performance", title: "Agent performance" },
  { href: "/coach/team-performance", title: "Team performance" },
  { href: "/coach/settings", title: "Settings" },
];

const config: Config = {
  navigation: {
    sidebar: [
      {
        title: "Admin",
        items: [
          {
            href: "/company-selection",
            icon: Building2,
            roles: new Set(["super", "owner"]),
            title: "Company selection",
          },
          {
            href: SUPER_PATHS.insights,
            icon: TrendingUp,
            title: "Insights",
            roles: new Set(["super"]),
          },
          {
            href: SUPER_PATHS.industryInsights,
            icon: PieChart,
            title: "Industry Insights",
            roles: new Set(["super"]),
          },
          {
            href: SUPER_PATHS.manageSettings,
            icon: DatabaseZap,
            title: "Manage settings",
            roles: new Set(["super"]),
          },
          {
            href: "/monitoring",
            icon: Activity,
            title: "Monitoring",
            roles: new Set(["super", "admin"]),
          },
          {
            href: "/semantic-search",
            icon: SearchCheck,
            title: "Semantic search",
            roles: new Set(["super", "admin"]),
          },
        ],
      },
      {
        title: "Platform",
        items: [
          {
            href: "/reports",
            icon: PieChart,
            title: "Reporting",
            roles: new Set(["super", "admin", "owner"]),
          },
          {
            href: "/conversations",
            icon: MessagesSquare,
            title: "Conversations",
          },
          {
            href: "/coach",
            icon: GraduationCap,
            title: "Coach",
            children: coachSubMenu,
          },
          {
            href: "/agents",
            icon: Users2,
            title: "Agents",
            roles: new Set(["super", "admin", "owner"]),
          },
          {
            href: "/agents/workflows",
            icon: GitBranch,
            title: "Workflows",
            roles: new Set(["super", "admin", "owner"]),
          },
          {
            href: "/agents/playbooks",
            icon: NotebookPen,
            title: "Playbooks",
          },
          {
            href: "/campaigns",
            icon: LandPlot,
            title: "Campaigns",
            roles: new Set(["super", "admin", "owner"]),
          },
          {
            href: "/settings",
            icon: Settings,
            title: "Settings",
            roles: new Set(["super", "admin", "owner"]),
            children: settingsSubMenu,
          },
        ],
      },
      {
        title: "Settings",
        items: [
          {
            href: "/integrations",
            icon: Puzzle,
            title: "Integrations",
            roles: new Set(["super", "admin", "owner"]),
          },
        ],
      },
    ],
    subMenus: {
      settings: settingsSubMenu,
      coach: coachSubMenu,
      phone: phoneSubMenu,
    },
  },
  industry: {
    options: ["HVAC", "Pest control", "Plumbing", "Roofing", "Other"],
  },
  backgroundSound: {
    options: [
      { id: "off", name: "Off" },
      { id: "office", name: "Office" },
    ],
  },
  language: {
    options: [
      { key: "en", label: "English" },
      { key: "es", label: "Spanish" },
      { key: "fr", label: "French" },
      { key: "de", label: "German" },
      { key: "it", label: "Italian" },
      { key: "pt", label: "Portuguese" },
      { key: "zh", label: "Chinese" },
      { key: "ja", label: "Japanese" },
      { key: "ko", label: "Korean" },
      { key: "ru", label: "Russian" },
      { key: "tr", label: "Turkish" },
      { key: "vi", label: "Vietnamese" },
    ],
  },
};

export default config;
